import { useCallback, useEffect, useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr'
import { buyNowListing } from '../../api-client/listing'
import Router from 'next/router'
import { ROUTES } from '../../constants'
import { Button } from '../button'
import Modal from '../modal'
import PurchaseMade from '../purchase-made'
import { PriceComponentType } from '../../api-client/enums'

const VIEWS = {
  Buying: 1,
  PurchaseMade: 2,
}

export default function BuyNowModal({
  onClose,
  vehicleId,
  buyNowListingId,
  vehicleName,
  price,
  shipmentRate,
}) {
  const [view, setView] = useState(VIEWS.Buying)
  const [canGoToPurchasePage, setCanGoToPurchasePage] = useState(false)
  const goToPurchasePage = () => Router.push(`${ROUTES.PurchaseBuyNow}/${buyNowListingId}`)

  const { mutate: mutateBuyNow, isLoading } = useMutation({
    mutationFn: buyNowListing,
    onSuccess: () => setView(VIEWS.PurchaseMade),
  })

  const signalrConRef = useRef(null)

  const buyFee = Number(process.env.NEXT_PUBLIC_BUY_FEE) || 0
  const shipmentAmount = !!shipmentRate && shipmentRate.isSucceeded ? shipmentRate.rate : 0
  const fullAmount = price + buyFee + shipmentAmount

  const acceptedPrices = [
    { amount: price, type: PriceComponentType.VehiclePrice },
    { amount: buyFee, type: PriceComponentType.ServiceFee },
    { amount: shipmentAmount, type: PriceComponentType.ShippingFee },
  ]

  const setUpSignalRConnection = useCallback(async (vehicleId) => {
    const connection = new HubConnectionBuilder()
      .withUrl(process.env.NEXT_PUBLIC_SIGNALR_LISTING_DOMAIN)
      .withAutomaticReconnect()
      .build()

    connection.on('VehicleSold', () => {
      setCanGoToPurchasePage(true)
    })

    try {
      await connection.start()
    } catch (err) {
      alert("SignalR couldn't connect")
      console.log("SignalR couldn't connect: " + err)
    }

    if (connection.state === HubConnectionState.Connected) {
      connection.invoke('AddToGroup', vehicleId).catch((err) => {
        console.log("SignalR couldn't AddToGroup " + err)
      })
    }
    return connection
  }, [])

  useEffect(() => {
    if (!signalrConRef.current) {
      setUpSignalRConnection(vehicleId).then((con) => {
        signalrConRef.current = con
      })
    }

    return () => signalrConRef.current?.stop()
  }, [setUpSignalRConnection, vehicleId])

  return (
    <Modal
      onClose={onClose}
      closeOnClickOutside={view !== VIEWS.PurchaseMade}
      hideCloseBtn={view === VIEWS.PurchaseMade}
    >
      <div className='wrapper'>
        {view === VIEWS.Buying ? (
          <>
            <h2 className='title'>Buy this vehicle?</h2>
            <p className='description'>
              You are buying <span className='vehicleName'>{vehicleName}</span> for{' '}
              <span className='price'>
                {fullAmount.toLocaleString('en-US', {
                  style: 'currency',
                  currency: 'USD',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}
              </span>
            </p>

            <div className='buttonsContainer flex justifyCenter'>
              <Button color='secondary' disabled={isLoading} onClick={onClose}>
                Back
              </Button>
              <Button
                disabled={isLoading}
                onClick={() => mutateBuyNow({ buyNowListingId, acceptedPrices })}
              >
                Buy
              </Button>
            </div>
          </>
        ) : (
          <PurchaseMade forBuyer isDisabled={!canGoToPurchasePage} onClose={goToPurchasePage} />
        )}
      </div>

      <style jsx>{`
        .wrapper {
          max-width: 92vw;
          width: 43.6rem;
          padding: 2.6rem 0 3.5rem;
          border-radius: 1rem;
          background: #fff;
        }

        .title {
          text-align: center;
          font-size: 3.2rem;
        }

        .description {
          max-width: 34rem;
          width: 100%;
          margin: 1.3rem auto 0;
          text-align: center;
          font-size: 1.6rem;
        }

        .vehicleName {
          font-weight: 700;
        }

        .price {
          color: var(--primary-color);
        }

        .buttonsContainer {
          width: 100%;
          margin: 2rem 0 0;

          :global(button) {
            width: 14.4rem;
            margin: 0 0.35rem;
          }
        }

        @media (max-width: 1024px) {
          .wrapper {
            max-width: 100%;
            width: 100%;
            height: 100%;
            padding: 5rem 1rem 0;
          }

          .title {
            font-size: 1.8rem;
          }

          .buttonsContainer {
            position: fixed;
            bottom: 2.1rem;
            z-index: 1;
            width: calc(100vw - 2.6rem);
            margin: 0;

            :global(button) {
              width: 100%;
            }
          }
        }
      `}</style>
    </Modal>
  )
}
